import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Pencil, Plus, Trash2, Search } from "lucide-react";
import { toast } from "sonner";
import SelectWithAdd from "@/components/SelectWithAdd";
import MultiSelectWithAdd from "@/components/MultiSelectWithAdd";
import DatePickerField from "@/components/DatePickerField";
import AddNewDialog from "@/components/AddNewDialog";
import AddCustomerDialog from "@/components/AddCustomerDialog";
import BulkUploadButton from "@/components/BulkUploadButton";
import SearchableSelect from "@/components/SearchableSelect";
import { useAuth } from "@/contexts/AuthContext";
import { Th, Td } from "@/pages/CustomersPage";
import { format, parseISO } from "date-fns";
import { safeFormatDate } from "@/lib/utils";

const EMPTY = {
  device_id: "",
  sim_service_provider_id: "",
  sim_service_provider_name: "",
  mobile_number: "",
  sim_no: "",
  device_model_id: "",
  device_model_name: "",
  installation_type_ids: [],
  installation_type_names: [],
  device_imei_number: "",
  customer_id: "",
  customer_name: "",
  vehicle_number: "",
  invoice_type: "Monthly",
  installation_date: "",
  installed_employee_id: "",
  installed_employee_name: "",
  next_invoice_date: "",
  sim_renewal_date: "",
  remarks: "",
};

const INVOICE_TYPES = [
  { id: "Monthly", label: "Monthly" },
  { id: "Yearly", label: "Yearly" },
];

export default function VehiclesPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  // master lists
  const [providers, setProviders] = useState([]);
  const [sims, setSims] = useState([]);
  const [availableDevices, setAvailableDevices] = useState([]);
  const [deviceModels, setDeviceModels] = useState([]);
  const [installTypes, setInstallTypes] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [employees, setEmployees] = useState([]);

  // inline add dialogs
  const [addProviderOpen, setAddProviderOpen] = useState(false);
  const [addDeviceOpen, setAddDeviceOpen] = useState(false);
  const [addInstallTypeOpen, setAddInstallTypeOpen] = useState(false);
  const [addEmployeeOpen, setAddEmployeeOpen] = useState(false);
  const [addCustomerOpen, setAddCustomerOpen] = useState(false);
  const [countries, setCountries] = useState([]);

  const loadVehicles = () => api.get("/vehicles").then((r) => setItems(r.data));
  const loadProviders = () => api.get("/providers").then((r) => setProviders(r.data));
  const loadSims = () => api.get("/sims").then((r) => setSims(r.data));
  const loadDevices = () => api.get("/device-models").then((r) => setDeviceModels(r.data));
  const loadAvailableDevices = (excludeVehicleId) => {
    const q = excludeVehicleId ? `?exclude_vehicle_id=${excludeVehicleId}` : "";
    return api.get(`/device-details/available${q}`).then((r) => setAvailableDevices(r.data));
  };
  const loadInstallTypes = () => api.get("/installation-types").then((r) => setInstallTypes(r.data));
  const loadCustomers = () => api.get("/customers").then((r) => setCustomers(r.data));
  const loadEmployees = () => api.get("/employees").then((r) => setEmployees(r.data));

  // Initial data load — all loadX helpers reference stable `api` and setState
  // dispatchers. Intentionally one-shot.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    loadVehicles();
    loadProviders();
    loadSims();
    loadDevices();
    loadInstallTypes();
    loadCustomers();
    loadEmployees();
    api.get("/geo/countries").then((r) => setCountries(r.data));
  }, []);

  const openNew = () => { setEditing(null); setForm(EMPTY); loadAvailableDevices(); setOpen(true); };
  const openEdit = (row) => {
    setEditing(row);
    setForm({
      ...EMPTY,
      ...row,
      installation_type_ids: row.installation_type_ids || [],
      installation_type_names: row.installation_type_names || [],
    });
    loadAvailableDevices(row.id);
    setOpen(true);
  };

  const setDeviceAssignment = (deviceId) => {
    const d = availableDevices.find((x) => x.id === deviceId);
    setForm((f) => ({
      ...f,
      device_id: deviceId,
      device_model_id: d?.device_model_id || "",
      device_model_name: d?.device_model_name || "",
      device_imei_number: d?.device_imei_number || "",
      sim_service_provider_id: d?.sim_service_provider_id || "",
      sim_service_provider_name: d?.sim_service_provider_name || "",
      mobile_number: d?.mobile_number || "",
      sim_no: d?.sim_no || "",
    }));
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.vehicle_number || !form.customer_id) {
      toast.error("Vehicle Number, Customer and SIM Provider are required");
      return;
    }
    setSaving(true);
    try {
      if (editing) await api.put(`/vehicles/${editing.id}`, form);
      else await api.post("/vehicles", form);
      toast.success(editing ? "Vehicle updated" : "Vehicle created");
      setOpen(false);
      loadVehicles();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete vehicle ${row.vehicle_number}?`)) return;
    try {
      await api.delete(`/vehicles/${row.id}`);
      toast.success("Deleted");
      loadVehicles();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  };

  const filtered = useMemo(() => {
    const f = filter.toLowerCase();
    return items.filter((r) =>
      !f ||
      [r.vehicle_number, r.customer_name, r.mobile_number, r.sim_no, r.device_imei_number, r.sim_service_provider_name]
        .some((x) => (x || "").toLowerCase().includes(f))
    );
  }, [items, filter]);

  // Inline add handlers
  const addProvider = async (vals) => {
    const { data } = await api.post("/providers", vals);
    await loadProviders();
    setForm((f) => ({ ...f, sim_service_provider_id: data.id, sim_service_provider_name: data.name }));
    toast.success("Provider added");
  };
  const addDevice = async (vals) => {
    const { data } = await api.post("/device-models", vals);
    await loadDevices();
    setForm((f) => ({ ...f, device_model_id: data.id, device_model_name: `${data.manufacturer} ${data.model}` }));
    toast.success("Device model added");
  };
  const addInstallType = async (vals) => {
    const { data } = await api.post("/installation-types", vals);
    await loadInstallTypes();
    setForm((f) => ({
      ...f,
      installation_type_ids: [...f.installation_type_ids, data.id],
      installation_type_names: [...f.installation_type_names, data.name],
    }));
    toast.success("Installation type added");
  };
  const addEmployee = async (vals) => {
    const { data } = await api.post("/employees", vals);
    await loadEmployees();
    setForm((f) => ({ ...f, installed_employee_id: data.id, installed_employee_name: data.name }));
    toast.success("Employee added");
  };

  // helpers to wire SelectWithAdd
  const setProvider = (id) => {
    const p = providers.find((x) => x.id === id);
    // changing provider clears the dependent mobile/sim selection
    setForm({
      ...form,
      sim_service_provider_id: id,
      sim_service_provider_name: p?.name || "",
      mobile_number: "",
      sim_no: "",
    });
  };
  const setMobile = (mobile) => {
    // find matching sim by provider + mobile and auto-fill sim_no
    const match = sims.find(
      (s) =>
        s.sim_service_provider_id === form.sim_service_provider_id &&
        s.mobile_number === mobile
    );
    setForm({
      ...form,
      mobile_number: mobile,
      sim_no: match?.sim_no || "",
    });
  };
  const setDevice = (id) => {
    const d = deviceModels.find((x) => x.id === id);
    setForm({ ...form, device_model_id: id, device_model_name: d ? `${d.manufacturer} ${d.model}` : "" });
  };
  const setCustomer = (id) => {
    const c = customers.find((x) => x.id === id);
    setForm({ ...form, customer_id: id, customer_name: c?.name || "" });
  };
  const setEmployee = (id) => {
    const e = employees.find((x) => x.id === id);
    setForm({ ...form, installed_employee_id: id, installed_employee_name: e?.name || "" });
  };
  const selectInstallTypes = (ids) => {
    const names = installTypes.filter((x) => ids.includes(x.id)).map((x) => x.name);
    setForm({ ...form, installation_type_ids: ids, installation_type_names: names });
  };

  return (
    <div data-testid="vehicles-page">
      <PageHeader
        overline="Operations"
        title="Customer Vehicle Installation Details"
        description="Each record maps a SIM card, device, and installation to a customer vehicle."
        actions={
          <div className="flex gap-2">
            <BulkUploadButton
              templateUrl="/vehicles/sample-template"
              uploadUrl="/vehicles/bulk-upload"
              templateName="vehicle_installations_template.xlsx"
              label="Bulk Upload"
              testIdPrefix="vehicle-bulk"
              onDone={loadVehicles}
            />
            <Button
              data-testid="vehicle-add-button"
              onClick={openNew}
              className="rounded-none vsm-primary-bg hover:opacity-90"
            >
              <Plus className="w-4 h-4 mr-2" /> New Vehicle Record
            </Button>
          </div>
        }
      />

      <div className="px-4 sm:px-6 md:px-10 py-5 sm:py-8 space-y-6">
        <div className="flex items-center gap-3">
          <div className="relative max-w-md w-full">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 vsm-muted" />
            <Input
              data-testid="vehicle-quick-search"
              className="rounded-none pl-9"
              placeholder="Quick search by vehicle, customer, SIM, IMEI…"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
            />
          </div>
          <div className="vsm-overline ml-auto">{filtered.length} record(s)</div>
        </div>

        <div className="border vsm-border vsm-surface overflow-x-auto vsm-table-scroll" data-testid="vehicles-table">
          <table className="w-full text-sm whitespace-nowrap">
            <thead>
              <tr className="border-b vsm-border text-left">
                <Th>Vehicle #</Th>
                <Th>Customer</Th>
                <Th>SIM Provider</Th>
                <Th>SIM Number</Th>
                <Th>Device</Th>
                <Th>Installed</Th>
                <Th>Next Invoice</Th>
                <Th>SIM Renewal</Th>
                <Th>Invoice</Th>
                <Th className="text-right">Actions</Th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={10} className="px-4 py-10 vsm-muted text-center">No vehicle records yet.</td>
                </tr>
              )}
              {filtered.map((r) => (
                <tr key={r.id} className="border-b vsm-border hover:bg-zinc-50 dark:hover:bg-zinc-800/40">
                  <Td className="font-semibold">{r.vehicle_number}</Td>
                  <Td>{r.customer_name}</Td>
                  <Td>{r.sim_service_provider_name}</Td>
                  <Td>{r.mobile_number}</Td>
                  <Td>{r.device_model_name}</Td>
                  <Td>{safeFormatDate(r.installation_date)}</Td>
                  <Td>{safeFormatDate(r.next_invoice_date)}</Td>
                  <Td>{safeFormatDate(r.sim_next_renewal_date)}</Td>
                  <Td>{r.invoice_type}</Td>
                  <Td className="text-right">
                    <button onClick={() => openEdit(r)} data-testid={`vehicle-edit-${r.id}`} className="p-1.5 hover:bg-zinc-100 dark:hover:bg-zinc-800 mr-1">
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button onClick={() => remove(r)} data-testid={`vehicle-delete-${r.id}`} className="p-1.5 hover:bg-red-100 hover:text-red-700" disabled={!isAdmin} style={!isAdmin ? {display: 'none'} : undefined}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="rounded-none border vsm-border max-w-4xl max-h-[90vh] overflow-y-auto"
          data-testid="vehicle-dialog"
        >
          <DialogHeader>
            <div className="vsm-overline">{editing ? "Edit Record" : "New Record"}</div>
            <DialogTitle className="text-2xl font-bold tracking-tight">
              {editing ? `Edit ${editing.vehicle_number}` : "New Customer Vehicle"}
            </DialogTitle>
            <DialogDescription className="vsm-muted text-sm">
              Pick a Device from the available stock (devices not yet fitted to a vehicle). All other hardware fields are auto-filled from the device.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={save} className="space-y-6 mt-2">
            <Fieldset legend="Customer & Vehicle">
              <FormGrid>
                <Field label="Customer" required>
                  <SelectWithAdd
                    testId="vehicle-customer"
                    value={form.customer_id}
                    onChange={setCustomer}
                    options={customers
                      .filter((c) => (c.status || "Active") === "Active")
                      .map((c) => ({ id: c.id, label: `${c.name} · ${c.city}` }))}
                    onAddNew={() => setAddCustomerOpen(true)}
                    placeholder="Select customer"
                  />
                </Field>
                <Field label="Vehicle Number" required>
                  <Input data-testid="vehicle-number" className="rounded-none" value={form.vehicle_number} onChange={(e) => setForm({ ...form, vehicle_number: e.target.value })} />
                </Field>
              </FormGrid>
            </Fieldset>

            <Fieldset legend="Device Assignment">
              <FormGrid>
                <Field label="Device (IMEI · Model · SIM)" className="md:col-span-3">
                  <SelectWithAdd
                    testId="vehicle-device"
                    value={form.device_id}
                    onChange={setDeviceAssignment}
                    options={availableDevices.map((d) => ({
                      id: d.id,
                      label: `${d.device_imei_number} · ${d.device_model_name}${d.mobile_number ? " · " + d.mobile_number : " · (no SIM)"}`,
                    }))}
                    placeholder="Select an available device"
                  />
                </Field>
                <Field label="Device Model">
                  <Input className="rounded-none bg-zinc-100 dark:bg-zinc-800" value={form.device_model_name} readOnly placeholder="Auto-filled" />
                </Field>
                <Field label="Device IMEI">
                  <Input className="rounded-none bg-zinc-100 dark:bg-zinc-800 font-mono" value={form.device_imei_number} readOnly placeholder="Auto-filled" />
                </Field>
                <Field label="SIM Service Provider">
                  <Input className="rounded-none bg-zinc-100 dark:bg-zinc-800" value={form.sim_service_provider_name} readOnly placeholder="From device's SIM" />
                </Field>
                <Field label="SIM Number">
                  <Input className="rounded-none bg-zinc-100 dark:bg-zinc-800 font-mono" value={form.mobile_number} readOnly placeholder="From device's SIM" />
                </Field>
                <Field label="SIM Number">
                  <Input className="rounded-none bg-zinc-100 dark:bg-zinc-800 font-mono" value={form.sim_no} readOnly placeholder="From device's SIM" />
                </Field>
                <Field label="Installation Types" className="md:col-span-3">
                  <MultiSelectWithAdd
                    testId="vehicle-install-types"
                    value={form.installation_type_ids}
                    onChange={selectInstallTypes}
                    options={installTypes.map((t) => ({ id: t.id, label: t.name }))}
                    onAddNew={() => setAddInstallTypeOpen(true)}
                    placeholder="Pick one or more (Basic, Fuel, Temperature, Relay)"
                  />
                </Field>
              </FormGrid>
            </Fieldset>

            <Fieldset legend="Installation & Invoice">
              <FormGrid>
                <Field label="Installed Employee">
                  <SelectWithAdd
                    testId="vehicle-employee"
                    value={form.installed_employee_id}
                    onChange={setEmployee}
                    options={employees.map((e) => ({ id: e.id, label: e.name }))}
                    onAddNew={() => setAddEmployeeOpen(true)}
                    placeholder="Select employee"
                  />
                </Field>
                <Field label="Installation Date">
                  <DatePickerField
                    testId="vehicle-installation-date"
                    value={form.installation_date}
                    onChange={(v) => setForm({ ...form, installation_date: v })}
                  />
                </Field>
                <Field label="Next Invoice Date">
                  <DatePickerField
                    testId="vehicle-next-invoice-date"
                    value={form.next_invoice_date}
                    onChange={(v) => setForm({ ...form, next_invoice_date: v })}
                  />
                </Field>
                <Field label="Remarks" className="sm:col-span-2">
                  <textarea
                    data-testid="vehicle-remarks"
                    className="w-full rounded-none border vsm-border px-3 py-2 text-sm vsm-surface focus:outline-none focus:ring-1 focus:ring-blue-500"
                    rows={3}
                    placeholder="Free-form notes, condition, special instructions…"
                    value={form.remarks || ""}
                    onChange={(e) => setForm({ ...form, remarks: e.target.value })}
                  />
                </Field>
              </FormGrid>
            </Fieldset>

            {editing && (
              <div className="border vsm-border p-4 text-xs vsm-muted grid grid-cols-2 gap-2">
                <div>Added by: <span className="font-mono">{editing.added_by}</span></div>
                <div>Added: <span className="font-mono">{editing.added_date}</span></div>
                <div>Modified by: <span className="font-mono">{editing.modified_by}</span></div>
                <div>Modified: <span className="font-mono">{editing.modified_date}</span></div>
              </div>
            )}

            <DialogFooter className="pt-2">
              <Button type="button" variant="outline" className="rounded-none" onClick={() => setOpen(false)} data-testid="vehicle-cancel">Cancel</Button>
              <Button type="submit" disabled={saving} className="rounded-none vsm-primary-bg hover:opacity-90" data-testid="vehicle-save">
                {saving ? "Saving…" : "Save Record"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AddNewDialog
        open={addProviderOpen}
        onOpenChange={setAddProviderOpen}
        title="Add Provider"
        fields={[
          { key: "name", label: "Provider Name", required: true },
          { key: "account_number", label: "Account Number", required: true },
        ]}
        onSubmit={addProvider}
        testIdPrefix="add-provider"
      />
      <AddNewDialog
        open={addDeviceOpen}
        onOpenChange={setAddDeviceOpen}
        title="Add Device Model"
        fields={[
          { key: "manufacturer", label: "Manufacturer", required: true },
          { key: "model", label: "Model", required: true },
        ]}
        onSubmit={addDevice}
        testIdPrefix="add-device"
      />
      <AddNewDialog
        open={addInstallTypeOpen}
        onOpenChange={setAddInstallTypeOpen}
        title="Add Installation Type"
        fields={[{ key: "name", label: "Type Name", required: true }]}
        onSubmit={addInstallType}
        testIdPrefix="add-install-type"
      />
      <AddNewDialog
        open={addEmployeeOpen}
        onOpenChange={setAddEmployeeOpen}
        title="Add Employee"
        fields={[{ key: "name", label: "Employee Name", required: true }]}
        onSubmit={addEmployee}
        testIdPrefix="add-employee"
      />
      <AddCustomerDialog
        open={addCustomerOpen}
        onOpenChange={setAddCustomerOpen}
        countries={countries}
        onCreated={async (created) => {
          await loadCustomers();
          setForm((f) => ({ ...f, customer_id: created.id, customer_name: created.name }));
        }}
      />
    </div>
  );
}

function Fieldset({ legend, children }) {
  return (
    <fieldset className="vsm-fieldset">
      <legend className="vsm-fieldset-legend">{legend}</legend>
      {children}
    </fieldset>
  );
}

function FormGrid({ children }) {
  return <div className="grid grid-cols-1 md:grid-cols-3 gap-4">{children}</div>;
}

function Field({ label, required, children, className = "" }) {
  return (
    <div className={className}>
      <Label className="vsm-overline">
        {label} {required && <span className="text-red-600">*</span>}
      </Label>
      <div className="mt-2">{children}</div>
    </div>
  );
}
